import { useRef, useState } from 'react';
import { api, cn, Spinner } from '@karozik/ui';
import { FaceCropper, type CroppedFace } from './FaceCropper';
import { loadLandmarker } from './faceLandmarks';

/**
 * « Ajoute ta tête » — the guest's selfie for Têtes Mêlées. The server keeps it
 * in memory for the night only; the guest can change or remove it at any time.
 */
export function SelfieCard({ joinToken, compact = false }: { joinToken: string; compact?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState(0);
  const [has, setHas] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `/api/join/${joinToken}/face`;

  const upload = async (face: CroppedFace | null) => {
    setFile(null);
    if (!face) return;
    setBusy(true);
    try {
      await api(url, { method: 'PUT', json: face });
      setError(null);
      setVersion((v) => v + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api(url, { method: 'DELETE' });
      setHas(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={cn('rounded-3xl bg-surface', compact ? 'p-3' : 'mt-4 p-4')}>
      <div className="flex items-center gap-3">
        <div className="relative h-16 w-[3.2rem] shrink-0 overflow-hidden rounded-xl bg-surface-2">
          <img
            key={version}
            src={`${url}?v=${version}`}
            alt=""
            onLoad={() => setHas(true)}
            onError={() => setHas(false)}
            className={cn('h-full w-full object-cover', !has && 'hidden')}
          />
          {!has && <div className="flex h-full items-center justify-center text-2xl">🤪</div>}
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center bg-night/60">
              <Spinner />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold">{has ? 'Ta tête est dans le jeu' : 'Têtes Mêlées : ajoute ta tête'}</div>
          <p className="text-xs text-ink-dim">
            {has
              ? 'Elle sera peut-être mélangée avec celle d’un autre invité sur l’écran géant.'
              : 'Un selfie, et ta tête pourra être mélangée avec celle d’un autre invité. À la salle de deviner !'}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => {
            // Warm the face detector up while the camera is open: by the time
            // the photo comes back, the model is downloaded and compiled.
            void loadLandmarker();
            input.current?.click();
          }}
          disabled={busy}
          className={cn('flex-[2] rounded-2xl py-2.5 font-bold transition active:scale-[0.98] disabled:opacity-40', has ? 'bg-surface-2' : 'bg-gradient-neon')}
        >
          📸 {has ? 'Changer de selfie' : 'Prendre mon selfie'}
        </button>
        {has && (
          <button onClick={() => void remove()} disabled={busy} className="flex-1 rounded-2xl bg-surface-2 py-2.5 text-sm text-ink-dim disabled:opacity-40">
            Retirer
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
      {!compact && (
        <p className="mt-2 text-[11px] leading-snug text-ink-faint">
          Ta photo sert uniquement à ce jeu. Elle reste en mémoire le temps de la soirée, n’est jamais enregistrée et s’efface à la fin.
        </p>
      )}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) setFile(f);
        }}
      />
      {file && <FaceCropper file={file} onDone={(face) => void upload(face)} />}
    </section>
  );
}
